import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches render-phase exceptions in any child and
 * shows an ASCII fallback panel with reload + return-home actions instead of
 * a blank screen. Wrap the router (or any subtree you want isolated) in this.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const offline = typeof navigator !== 'undefined' && !navigator.onLine;

    return (
      <div className="public-shell" role="alert" aria-live="assertive">
        <div className="brand">
          <h1>AstdZ<span style={{ color: 'var(--color-accent)' }}>_</span></h1>
          <div className="tag">{offline ? 'offline' : 'something broke'}</div>
        </div>
        <div className="ascii-rule">━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>
        <div className="panel">
          <p className="text-muted">
            {offline
              ? 'you appear to be offline. check your connection and try again.'
              : 'an unexpected error occurred while rendering this page.'}
          </p>
          {this.state.error.message && (
            <pre style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-muted)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 'var(--spacing-sm) 0',
            }}>
              {this.state.error.message}
            </pre>
          )}
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              reload
            </button>
            <button type="button" className="btn" onClick={() => { window.location.href = '/dashboard'; }}>
              return home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
